"use client";

import { useCallback } from "react";
import { resolveAnchorTarget } from "@/utils/anchorId";
import { findScrollTargetElement } from "@/utils/scrollAnchor";
import { useScrollToAnchor } from "@/hooks/useScrollToAnchor";

/**
 * Returns an onClick handler that smooth-scrolls to in-page anchors. Non-anchor
 * links are left to the browser/default navigation.
 */
export function useAnchorClickHandler(href?: string) {
  const scrollToAnchor = useScrollToAnchor();

  return useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      if (!href) return;

      // Let the browser handle modified or non-primary clicks (open in new
      // tab/window, etc.) instead of hijacking them into a smooth scroll.
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }

      const anchorId = resolveAnchorTarget(href);
      if (!anchorId) return;

      const target = findScrollTargetElement(anchorId, {
        preferScheduler: anchorId === "book-demo",
      });
      if (!target) return;

      event.preventDefault();
      scrollToAnchor(href, {
        preferScheduler: anchorId === "book-demo",
      });
    },
    [href, scrollToAnchor],
  );
}
